// Calendar-day arithmetic that never goes through a UTC ISO string.
//
// `new Date().toISOString().slice(0, 10)` is the bug this module exists to make
// unavailable: it returns the UTC day, so from 5pm Pacific onward it reports
// tomorrow. The showtimes page keys everything off a date, so getting that
// wrong silently shows the wrong day's movies -- and only in the evening, which
// is exactly when someone is looking for a showing tonight.
//
// The date shown is the day in the THEATER's timezone, not the browser's. Those
// differ whenever you are travelling, and "what is playing today" means today
// where the cinema is.

/** `YYYY-MM-DD` for the current moment in `timeZone`. */
export function todayInTimeZone(timeZone: string | undefined, now: Date = new Date()): string {
  // `en-CA` formats as YYYY-MM-DD natively, so there is no reassembly step to
  // get wrong. Intl does the timezone conversion, which is the part hand-rolled
  // offset math gets wrong twice a year.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

/** `YYYY-MM-DD` for the current moment in the BROWSER's own zone.
 *
 *  Separate from `todayInTimeZone` because it answers a different question,
 *  not because it is a fallback. The planner is one list across every city, so
 *  there is no single theater zone to anchor "is this plan in the past?"
 *  against; the browser's day is the only defensible client-side answer, and
 *  the server settles it authoritatively afterwards.
 *
 *  Same Intl mechanism, deliberately: omitting `timeZone` uses the runtime's
 *  own, so this stays one implementation of the UTC-slice fix rather than a
 *  second one built out of `getFullYear`/`getMonth`/`getDate`.
 */
export function todayLocal(now: Date = new Date()): string {
  // Delegates rather than repeating the formatter: per ECMA-402, an absent
  // `timeZone` resolves to the runtime's own, so this is the SAME code path
  // with one option dropped, not a second implementation to keep in step.
  return todayInTimeZone(undefined, now);
}

function parseAsUtcNoon(date: string): Date {
  const [y, m, d] = date.split("-").map(Number);
  // NOON, not midnight, and that is the whole trick. `new Date("2026-03-08")`
  // is UTC midnight, which in Pacific is 4pm the PREVIOUS day -- so adding a
  // day and formatting locally can land back where it started. Noon is further
  // from both boundaries than any real offset (max +/-14h) can reach.
  return new Date(Date.UTC(y, m - 1, d, 12));
}

/** `date` shifted by `days`, staying a plain calendar date. */
export function shiftDate(date: string, days: number): string {
  const dt = parseAsUtcNoon(date);
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

/** True when `date` is the current day in `timeZone`. */
export function isToday(date: string, timeZone: string, now: Date = new Date()): boolean {
  return date === todayInTimeZone(timeZone, now);
}

// Both formatters render in UTC, NOT in a caller-supplied zone, and that is
// deliberate. The argument is already a calendar date -- the zone conversion
// that produced it happened in `todayInTimeZone`. Converting again while
// formatting re-introduces the very off-by-one this module exists to prevent:
// the instant is anchored at UTC noon, so any zone at UTC+12 or beyond pushes
// it into the next day. Measured before the fix:
//   formatLongDate("2026-08-09", "Pacific/Auckland") -> "Monday, August 10"
// which is reachable, because `Showtimes.tsx` falls back to the BROWSER's zone
// until /api/cities resolves, and this app treats travelling as normal.
// Rendering the date it was handed is the only correct behaviour here.

/** "Saturday, August 8" for a heading. */
export function formatLongDate(date: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(parseAsUtcNoon(date));
}

/** "Sat, 8/8" for the compact day strip. */
export function formatShortDate(date: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    weekday: "short",
    month: "numeric",
    day: "numeric",
  }).format(parseAsUtcNoon(date));
}
